import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { EmptyList } from '../../../../shared/empty-list/EmptyList';
import { SpinLoader } from '../../../../shared/spin-loader/SpinLoader';
import { AddButton } from '../../../../shared/button/AddButton';
import TransparentButton from '../../../../shared/button/transparent-button/TransparentButton';
import { PageActionsRow } from '../../../../shared/headers/PageActionsRow';
import FriendlyDate from '../../../../shared/FriendlyDate';
import { TimeZoneNote } from '../../../../shared/time-zone-note/TimeZoneNote';
import { ModalContainer } from '../../../../shared/modal-container/ModalContainer';
import '../../../../shared/table/AlertsTable.scss';
import { ConfirmModal } from '../common/ConfirmModal';
import { CreateWorkspaceModalContent } from './CreateWorkspaceModalContent';
import {
    createWorkspace,
    deleteWorkspace,
    fetchWorkspaces,
    refreshWorkspace,
    resetWorkspace
} from '../redux/workspaces/actions';
import { getWorkspacesState } from '../redux/selectors';
import { getBrandsAndJurisdictions } from '../../../../jurisdiction/redux/selectors';
import { setJurisdictionByCode } from '../../../../jurisdiction/redux/actions';
import {
    redirectTo,
    redirectToAudit
} from '../../../../routing/redirectService';
import ShowIfPermission from '../../../../shared/show-if-permission/ShowIfPermission';
import { PermissionEnum } from '../../../../shared/models/permissions';
import { isSandboxJurisdiction } from '../simulation.model';
import {
    getRuleCategoryUrl,
    RuleCategory
} from '../../../../services/RuleCategory';
import { isAuditEnabled } from '../../../../shared/feature-flags';

const workspaceHeaders = [
    'Sandbox',
    'Copied from',
    'Created',
    'Rules refreshed',
    'Runs',
    ''
];

const WorkspaceAction = Object.freeze({
    REFRESH: 'REFRESH',
    RESET: 'RESET',
    DELETE: 'DELETE'
});

/* What each of the actions on a sandbox says to the user, before it is done. */
const describeAction = (action, workspace) => {
    const { code, sourceCode } = workspace;

    switch (action) {
        case WorkspaceAction.REFRESH:
            return {
                title: `Refresh the rules of ${code}`,
                confirmText: 'Refresh rules',
                message: `The rules and the risk configuration of the sandbox are copied again from ${sourceCode}, replacing what they are now. Changes made to the rules of the sandbox are lost. Its players, events and alerts are kept.`
            };
        case WorkspaceAction.RESET:
            return {
                title: `Delete the data of ${code}`,
                confirmText: 'Delete data',
                message: `All the simulated players, events and alerts of the sandbox, and the runs that made them, are deleted. Its rules are kept. This cannot be undone. It is refused while a run in the sandbox is queued or running.`
            };
        case WorkspaceAction.DELETE:
            return {
                title: `Delete the sandbox ${code}`,
                confirmText: 'Delete sandbox',
                message: `The sandbox, its rules and all its simulated data and runs are deleted. This cannot be undone. It is refused while a run in the sandbox is queued or running.`
            };
        default:
            return {};
    }
};

class WorkspacesContainer extends Component {
    constructor(props) {
        super(props);
        this.state = { pending: null };
        this.confirmRef = React.createRef();
        this.createRef = React.createRef();
    }

    componentDidMount() {
        this.props.fetchWorkspaces();
    }

    ask = (action, workspace) =>
        this.setState({ pending: { action, workspace } }, () =>
            this.confirmRef.current.open()
        );

    confirmPending = () => {
        const { pending } = this.state;
        if (!pending) {
            return;
        }
        const { code } = pending.workspace;

        switch (pending.action) {
            case WorkspaceAction.REFRESH:
                return this.props.refreshWorkspace({ code });
            case WorkspaceAction.RESET:
                return this.props.resetWorkspace({ code });
            case WorkspaceAction.DELETE:
                return this.props.deleteWorkspace({ code });
            default:
                return;
        }
    };

    handleCreate = ({ sourceCode }) => {
        this.props.createWorkspace({ sourceCode });
        this.createRef.current.closeModal();
    };

    /* Looks at the data of the sandbox where the rest of the application does: the selection at the top. */
    viewData = code => {
        this.props.setJurisdictionByCode(code);
        redirectTo('/dashboard');
    };

    /*
      Opens the rules of the sandbox, those of anti money laundering, which is where the page of the rules starts. The page is the same
      as for any jurisdiction and works on the selection at the top, so the other categories are reached from the alerts of the
      sandbox, as they are for a jurisdiction.
    */
    viewRules = code => {
        this.props.setJurisdictionByCode(code);
        redirectTo(
            '/modify-rules/' +
                getRuleCategoryUrl(RuleCategory.ANTI_MONEY_LAUNDERING)
        );
    };

    sourceJurisdictions = () =>
        _.reject(this.props.jurisdictions, jurisdiction =>
            isSandboxJurisdiction(jurisdiction)
        );

    renderRow = workspace => {
        const busy = !!this.props.busy[workspace.code];
        const {
            code,
            name,
            sourceCode,
            createdAt,
            lastRefreshedAt
        } = workspace;
        // The server refuses to reset or delete a sandbox while a run of it is queued or running
        const activeRuns = this.props.activeRuns[code] || 0;

        return (
            <tr key={code} className="data">
                <td>
                    <div>
                        <strong>{code}</strong>
                    </div>
                    <div className={'sim-muted'}>{name}</div>
                </td>
                <td>{sourceCode}</td>
                <td>
                    <FriendlyDate timestamp={createdAt} />
                </td>
                <td>
                    {lastRefreshedAt ? (
                        <FriendlyDate timestamp={lastRefreshedAt} />
                    ) : (
                        'Never'
                    )}
                </td>
                <td>
                    <div>{workspace.runCount}</div>
                    {activeRuns > 0 && (
                        <div className={'sim-muted active-runs'}>
                            {activeRuns} queued or running
                        </div>
                    )}
                </td>
                <td className={'sim-actions'}>
                    <TransparentButton
                        disabled={busy}
                        onClick={() => this.viewData(code)}
                    >
                        View data
                    </TransparentButton>
                    <TransparentButton
                        disabled={busy}
                        onClick={() => this.viewRules(code)}
                    >
                        Rules
                    </TransparentButton>
                    {isAuditEnabled() && (
                        <ShowIfPermission
                            permission={PermissionEnum.VIEW_AUDIT_LOG}
                        >
                            <TransparentButton
                                onClick={() => redirectToAudit(code)}
                            >
                                History
                            </TransparentButton>
                        </ShowIfPermission>
                    )}
                    <TransparentButton
                        disabled={busy}
                        onClick={() =>
                            this.ask(WorkspaceAction.REFRESH, workspace)
                        }
                    >
                        Refresh rules
                    </TransparentButton>
                    <TransparentButton
                        disabled={busy || activeRuns > 0}
                        onClick={() =>
                            this.ask(WorkspaceAction.RESET, workspace)
                        }
                    >
                        Delete data
                    </TransparentButton>
                    <TransparentButton
                        disabled={busy || activeRuns > 0}
                        onClick={() =>
                            this.ask(WorkspaceAction.DELETE, workspace)
                        }
                    >
                        Delete
                    </TransparentButton>
                </td>
            </tr>
        );
    };

    render() {
        const { workspaces, loaded } = this.props;
        const pending = this.state.pending;
        const description = pending
            ? describeAction(pending.action, pending.workspace)
            : {};

        return (
            <div className={'sim-workspaces'}>
                <div className={'sim-section-note'}>
                    A sandbox is a copy of a jurisdiction to play simulated
                    players in. It never shows in the dashboard, alerts or
                    players of the jurisdiction it was copied from.
                </div>
                <PageActionsRow>
                    <AddButton
                        key={'add-workspace'}
                        onClick={() => this.createRef.current.openModal()}
                        label={'New sandbox'}
                    />
                </PageActionsRow>

                <div className={'table'}>
                    <TimeZoneNote />
                    <table>
                        <tbody>
                            <tr className="headers">
                                {workspaceHeaders.map((header, i) => (
                                    <th key={i}>{header}</th>
                                ))}
                            </tr>
                            {!loaded ? (
                                <tr>
                                    <td colSpan={workspaceHeaders.length}>
                                        <SpinLoader />
                                    </td>
                                </tr>
                            ) : _.isEmpty(workspaces) ? (
                                <tr className="data">
                                    <td
                                        colSpan={workspaceHeaders.length}
                                        style={{ textAlign: 'center' }}
                                    >
                                        <EmptyList />
                                    </td>
                                </tr>
                            ) : (
                                workspaces.map(this.renderRow)
                            )}
                        </tbody>
                    </table>
                </div>

                <ModalContainer
                    ref={this.createRef}
                    title={'New sandbox'}
                    content={
                        <CreateWorkspaceModalContent
                            sourceJurisdictions={this.sourceJurisdictions()}
                            createWorkspace={this.handleCreate}
                        />
                    }
                />
                <ConfirmModal
                    ref={this.confirmRef}
                    title={description.title}
                    message={description.message}
                    confirmText={description.confirmText}
                    onConfirm={this.confirmPending}
                />
            </div>
        );
    }
}

const mapStateToProps = state => {
    const { data, loaded, busy, activeRuns } = getWorkspacesState(state);
    const { jurisdictions } = getBrandsAndJurisdictions(state) || {};

    return { workspaces: data, loaded, busy, activeRuns, jurisdictions };
};

const mapDispatchToProps = dispatch => ({
    fetchWorkspaces: () => dispatch(fetchWorkspaces()),
    createWorkspace: args => dispatch(createWorkspace(args)),
    refreshWorkspace: args => dispatch(refreshWorkspace(args)),
    resetWorkspace: args => dispatch(resetWorkspace(args)),
    deleteWorkspace: args => dispatch(deleteWorkspace(args)),
    setJurisdictionByCode: code => dispatch(setJurisdictionByCode(code))
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(WorkspacesContainer);
